"use client";

import { getBrowserSupabaseClient } from "@wtc/supabase";


/**
 * Global Auth Guard for Supabase
 * 
 * This is the MOST CONSOLIDATED approach possible:
 * - Single file that overrides the global createClient function
 * - Automatically guards ALL Supabase calls across the entire app
 * - ZERO changes needed to existing code
 * - Works with all existing useQuery/useMutation calls
 * - No provider setup needed
 */

// Store the original createClient function
const originalCreateClient = getBrowserSupabaseClient;

// Global authentication state (will be set by the auth context)
let globalAuthState = {
  isAuthenticated: false,
  isLoading: true,
};

// Track auth context calls to allow them to bypass the guard
let isAuthContextCall = false;
let authContextCallCount = 0;
let lastAuthContextCallTime = 0;

// Function to temporarily allow auth context calls
export function allowAuthContextCall<T>(fn: () => T): T {
  const now = Date.now();
  authContextCallCount++;
  
  // Prevent excessive auth context calls (more than 10 calls in 5 seconds)
  if (authContextCallCount > 10 && (now - lastAuthContextCallTime) < 5000) {
    console.warn("🚨 Too many auth context calls detected, blocking to prevent infinite loop");
    throw new Error("Too many auth context calls - preventing infinite loop");
  }
  
  lastAuthContextCallTime = now;
  
  const wasAuthContextCall = isAuthContextCall;
  isAuthContextCall = true;
  try {
    return fn();
  } finally {
    isAuthContextCall = wasAuthContextCall;
  }
}

// Reset auth context call tracking (call this when auth state stabilizes)
export function resetAuthContextCallTracking() {
  authContextCallCount = 0;
  lastAuthContextCallTime = 0;
}

// Function to update global auth state (called by auth context)
export function setGlobalAuthState(state: { isAuthenticated: boolean; isLoading: boolean }) {
  const previousState = { ...globalAuthState };
  globalAuthState = state;
  
  console.log("🔐 Global Auth State Updated:", {
    previous: previousState,
    current: globalAuthState,
    changed: {
      isAuthenticated: previousState.isAuthenticated !== state.isAuthenticated,
      isLoading: previousState.isLoading !== state.isLoading
    }
  });
}

// Memoized proxy to prevent infinite loops
let memoizedProxy: any = null;

// Override the global createClient function
export function createClient() {
  const client = originalCreateClient();

  // Create a memoized proxy that intercepts ALL method calls
  if (!memoizedProxy) {
    memoizedProxy = new Proxy(client, {
    get(target, prop) {
      const originalMethod = target[prop as keyof typeof target];
      
      // Auth methods are always available
      if (prop === "auth") {
        return originalMethod;
      }

      // For all other methods, check authentication first
      if (typeof originalMethod === "function") {
        return (...args: any[]) => {
          // Allow auth context calls to bypass the guard
          if (isAuthContextCall) {
            console.log(`🔓 Supabase ${String(prop)} call allowed - auth context call`);
            
return (originalMethod as any).apply(target, args);
          }

          // If still loading authentication, return a promise that resolves to empty data
          if (globalAuthState.isLoading) {
            console.warn(`🚫 Supabase ${String(prop)} call blocked - authentication still loading`);
            console.log("   📍 Call stack:", new Error().stack?.split("\n").slice(1, 4));
            console.log("   🔍 Auth state:", { isLoading: globalAuthState.isLoading, isAuthenticated: globalAuthState.isAuthenticated });
            
return Promise.resolve({ 
              data: null, 
              error: { 
                message: "Authentication still loading",
                code: "AUTH_LOADING",
                details: "User authentication is still being verified"
              } 
            });
          }

          // If not authenticated, return an error
          if (!globalAuthState.isAuthenticated) {
            console.warn(`🚫 Supabase ${String(prop)} call blocked - user not authenticated`);
            console.log("   📍 Call stack:", new Error().stack?.split("\n").slice(1, 4));
            console.log("   🔍 Auth state:", { isLoading: globalAuthState.isLoading, isAuthenticated: globalAuthState.isAuthenticated });
            
return Promise.resolve({ 
              data: null, 
              error: { 
                message: "User not authenticated",
                code: "AUTH_REQUIRED", 
                details: "User must be authenticated to access this resource"
              } 
            });
          }

          // If authenticated, proceed with the original method
          return (originalMethod as any).apply(target, args);
        };
      }

      // For non-function properties, return them as-is
      return originalMethod;
    }
    });
  }
  
  return memoizedProxy;
}
